import classNames from "classnames";
import { environment } from "models/Environment";
import { NavigationModel } from "models/NavigationModel";
import { usePermissions, useTranslations } from "providers";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { getMainNavItems } from "./getMainNavItems";
import { LayoutContext } from "./LayoutContext";

export const LayoutProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const i18n = useTranslations();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAdministrator, isBoardOwner } = usePermissions();

  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);

  const isExternal = useCallback((item: NavigationModel) => {
    const target = item.target;
    const href = item.href;
    return target === "_blank" || (href ? href.startsWith("http") : false);
  }, []);

  const handleNavClick = useCallback(
    (item: NavigationModel | string) => {
      let href;
      let target;
      let onClick;
      if (typeof item === "string") {
        href = item;
      } else {
        href = item.href;
        target = item.target;
        onClick = item.onClick;
      }

      if (href) {
        if (href.includes("://")) {
          if (target === "_blank") {
            window.open(href);
          } else {
            window.location.href = href;
          }
        } else {
          navigate(href);
        }
      } else if (onClick) {
        onClick();
      }
      setIsHamburgerOpen(false);
    },
    [navigate]
  );

  const appClassName = useMemo(
    () =>
      classNames(
        "app",
        `sbb-environment-${environment.shortName}`
        //pathname == AppRoutes.VotingDone || pathname == AppRoutes.VotingOpen ? "has-background" : ""
      ),
    []
  );

  const navItems = useMemo(
    () => getMainNavItems(i18n, pathname, isAdministrator, isBoardOwner),
    [i18n, pathname, isAdministrator, isBoardOwner]
  );

  const contextValue = useMemo(
    () => ({
      isExternal,
      handleNavClick,
      isFooterVisible,
      setIsFooterVisible,
      isHamburgerOpen,
      setIsHamburgerOpen,
      navItems,
    }),
    [isExternal, handleNavClick, isFooterVisible, isHamburgerOpen, navItems]
  );

  return (
    <LayoutContext.Provider value={contextValue}>
      <div className={appClassName}>{children}</div>
    </LayoutContext.Provider>
  );
};
